import type { EndpointBuilder } from "@reduxjs/toolkit/query";
import type { ClimateOption, FuelOption, InputTypeOption, Option, ValuedOption } from "@typedefs/modulesInterfaces";
import { BEModulesClassNames } from "@utils/moduleList";
import type { TagTypes } from "@app/apiSlice";
import type { ApiSliceType, Country, LandUseType, PublicApiSliceType } from "@typedefs/interfaces";
import { ModuleTypeRegistry } from "@utils/moduleTypeRegistry";
interface LandUseProps {
  moduleType?: number;
  climate?: number;
  moisture?: number;
}

interface GWPOption extends Option {
  id: number;
  name: string;
  co2: number;
  ch4: number;
  ch4_fossil: number;
  n2o: number;
}

interface DataSourceOption extends Option {
  short_name: string;
}


export function createDropdownOptionsApi(apiSlice: ApiSliceType | PublicApiSliceType) {
  const endpoints = (builder: EndpointBuilder<any, TagTypes, string>) => ({
    getTypesOfFishery: builder.query<Option[], void>({
      query: () => "fishery-types/",
    }),
    getFishCategories: builder.query<Option[], void>({
      query: () => "fish-types/",
    }),
    getSmallFishGearCategory: builder.query<Option[], { fishery_types?: number }>({
      query: ({ fishery_types }) => {
        const params = new URLSearchParams();
        if (fishery_types)
          params.append('fishery_types', fishery_types.toString());
        return `small-fishery-gear-types/${params.toString() ? `?${params.toString()}` : ''}`
      }
    }),
    getLargeFishGearCategories: builder.query<Option[], void>({
      query: () => "large-fishery-gear-types/",
    }),
    getCropTypes: builder.query<Option[], void>({
      query: () => "crop-types/",
    }),
    getTillageTypes: builder.query<Option[], void>({
      query: () => "tillage-management-types/",
    }),
    getOrganicInputTypes: builder.query<Option[], void>({
      query: () => "organic-input-types/",
    }),
    getMacroInputTypes: builder.query<Option[], void>({
      query: () => "macro-input-types/",
    }),
    getMacroInputTypeById: builder.query<Option, number>({
      query: (id) => `macro-input-types/${id}/`,
      providesTags: (_res, _error, id) => [{ type: "MacroInputTypes", id }],
    }),
    getResidueTypes: builder.query<Option[], void>({
      query: () => "residue-management-types/",
    }),
    getSoilTypes: builder.query<Option[], { moduleId?: number }>({
      query: ({ moduleId }) => {
        const params = new URLSearchParams();
        const moduleRegistry = ModuleTypeRegistry.getInstance();
        const coastalWetlands = moduleRegistry.getIdByClassName(BEModulesClassNames.CoastalWetlands);
        if (moduleId && moduleId === coastalWetlands)
          params.append('is_coastal', 'true');

        return `soil-types/${params.toString() ? `?${params.toString()}` : ''}`
      },
      providesTags: (result) => 
        result
          ? [...result.map(({ id }) => ({ type: 'SoilTypes' as TagTypes, id })), 'SoilTypes']
          : ['SoilTypes'],
    }),
    getSoilTypeById: builder.query<Option, number>({
      query: (id) => `soil-types/${id}/`,
      providesTags: (result, error, id) => [{ type: 'SoilTypes', id }],
    }),
    getClimateTypes: builder.query<ClimateOption[], void>({
      query: () => "climates/",
      providesTags: (result) => 
        result
          ? [...result.map(({ id }) => ({ type: 'ClimateTypes' as TagTypes, id })), 'ClimateTypes']
          : ['ClimateTypes'],
    }),
    getClimateById: builder.query<ClimateOption, number>({
      query: (id) => `climates/${id}/`,
      providesTags: (result, error, id) => [{ type: 'ClimateTypes', id }],
    }),
    getMoistureTypes: builder.query<Option[], void>({
      query: () => "moistures/",
      providesTags: (result) => 
        result
          ? [...result.map(({ id }) => ({ type: 'MoistureTypes' as TagTypes, id })), 'MoistureTypes']
          : ['MoistureTypes'],
    }),
    getMoistureById: builder.query<Option, number>({
      query: (id) => `moistures/${id}/`,
      providesTags: (result, error, id) => [{ type: 'MoistureTypes', id }],
    }),
    getGrasslandTypes: builder.query<Option[], void>({
      query: () => "grassland-management-types/",
    }),
    getProjectStatuses: builder.query<Option[], void>({
      query: () => "statuses/",
      providesTags: (result) => 
        result
          ? [...result.map(({ id }) => ({ type: 'ProjectStatuses' as TagTypes, id })), 'ProjectStatuses']
          : ['ProjectStatuses'],
    }),
    getStatusById: builder.query<Option, number>({
      query: (id) => `statuses/${id}/`,
      providesTags: (result, error, id) => [{ type: 'ProjectStatuses', id }],
    }),
    getCountries: builder.query<Country[], void>({
      query: () => "countries/",
      transformResponse: (response: Option[]) => response.sort((a, b) => a.name.localeCompare(b.name)),
      providesTags: (result) => 
        result
          ? [...result.map(({ id }) => ({ type: 'Countries' as TagTypes, id })), 'Countries']
          : ['Countries'],
    }),
    getCountryById: builder.query<Country, number>({
      query: (id) => `countries/${id}/`,
      providesTags: (result, error, id) => [{ type: 'Countries', id }],
    }),
    getGWPSources: builder.query<GWPOption[], void>({
      query: () => "global-warming-potentials/",
      providesTags: (result) => 
        result
          ? [...result.map(({ id }) => ({ type: 'GWPSources' as TagTypes, id })), 'GWPSources']
          : ['GWPSources'],
    }),
    getGWPById: builder.query<GWPOption, number>({
      query: (id) => `global-warming-potentials/${id}/`,
      providesTags: (result, error, id) => [{ type: 'GWPSources', id }],
    }),
    getLandUseTypes: builder.query<Option[], LandUseProps>({
      query: ({ moduleType, climate, moisture }) => {
        const params = new URLSearchParams();
        if (moduleType) params.append('module_type', moduleType.toString());
        if (climate) params.append('climate', climate.toString());
        if (moisture) params.append('moisture', moisture.toString());
        return `land-use-types/${params.toString() ? `?${params.toString()}` : ''}`
      }
    }),
    getLandUseTypeById: builder.query<LandUseType, number>({
      query: (id) => `land-use-types/${id}/`,
      providesTags: (_res, _error, id) => [{ type: "LandUseTypes", id }],
    }),
    getLivestockCategories: builder.query<Option[], void>({
      query: () => "livestock-category-types/",
    }),
    getLivestockProductionTypes: builder.query<Option[], void>({
      query: () => "livestock-production-types/",
    }),
    getManureManagementTypes: builder.query<Option[], void>({
      query: () => "manure-management-types/",
    }),
    getWaterManagementTypesBC: builder.query<Option[], void>({
      query: () => "water-management-types-before-cultivation/",
    }),
    getWaterManagementTypesAC: builder.query<Option[], void>({
      query: () => "water-management-types-after-cultivation/",
    }),
    getOrganicAmendmentTypes: builder.query<Option[], void>({
      query: () => "organic-amendment-types/",
    }),
    getDisturbanceTypes: builder.query<Option[], void>({
      query: () => "disturbance-types/",
    }),
    getForestTypes: builder.query<Option[], void>({
      query: () => "forest-types/",
    }),
    getForestConditionTypes: builder.query<Option[], void>({
      query: () => "forest-condition-types/",
    }),
    getFundingAgencies: builder.query<Option[], void>({
      query: () => "funding-agencies/",
      providesTags: ["FundingAgencies"],
    }),
    getWaterbodyTypes: builder.query<Option[], void>({
      query: () => "waterbody-types/",
    }),
    getTrophicTypes: builder.query<Option[], void>({
      query: () => "trophic-types/",
    }),
    getFuelTypes: builder.query<FuelOption[], { search?: string[], module_types?: number }>({
      query: ({ search, module_types }) => {
        const params = new URLSearchParams();
        if (search)
          search.forEach(term => params.append('s', term));
        if (module_types)
          params.append('module_types', module_types.toString());
        return {
          url: `fuel-types/${params.toString() ? `?${params.toString()}` : ''}`,
          headers: { 'Accept-Language': "en" } // FIXED THE LANGUAGE TO AVOID SEARCH FILTERING BUG
        }
      },
      // fuel_use_type kept on each option so the dropdown can render optgroup headings
    }),
    getSalinityTypes: builder.query<Option[], void>({
      query: () => 'salinity-types/',
      transformResponse: (response: ValuedOption[]) => response.map(({ id, value }) => ({ id, name: value.toString() }))
    }),
    getMacroFuelTypes: builder.query<Option[], void>({
      query: () => "macro-fuel-types/",
    }),
    getMacroFuelTypeById: builder.query<Option, number>({
      query: (id) => `macro-fuel-types/${id}/`,
      providesTags: (_res, _error, id) => [{ type: "MacroFuelTypes", id }],
    }),
    getPeatTypes: builder.query<Option[], void>({
      query: () => "peat-types/",
    }),
    getFuelUseTypes: builder.query<Option[], void>({
      query: () => "fuel-use-types/",
    }),
    getFuelUseTypeById: builder.query<Option, number>({
      query: (id) => `fuel-use-types/${id}/`,
      providesTags: (_res, _error, id) => [{ type: "FuelUseTypes", id }],
    }),
    getFireTypes: builder.query<Option[], void>({
      query: () => "fire-types/",
    }),
    getIrrigationSystemTypes: builder.query<Option[], { moduleTypes?: number }>({
      query: (params) => ({
        url: 'irrigation-system-types/',
        params: {
          module_types: params?.moduleTypes
        }
      })
    }),
    createFundingAgency: builder.mutation<Option, string>({
      query: (name) => ({
        url: "funding-agencies/",
        method: "POST",
        body: { name },
      }),
      invalidatesTags: ["FundingAgencies"],
    }),
    getExecutingAgencies: builder.query<Option[], void>({
      query: () => "executing-agencies/",
      providesTags: ["ExecutingAgencies"],
    }),
    createExecutingAgency: builder.mutation<Option, string>({
      query: (name) => ({
        url: "executing-agencies/",
        method: "POST",
        body: { name },
      }),
      invalidatesTags: ["ExecutingAgencies"],
    }),
    getRoadTypes: builder.query<Option[], void>({
      query: () => 'road-types/'
    }),
    getBuildingTypes: builder.query<Option[], void>({
      query: () => 'building-types/'
    }),
    getEmissionFactorSources: builder.query<Option[], void>({
      query: () => 'emission-factor-sources/'
    }),
    getSettlementTypes: builder.query<Option[], void>({
      query: () => 'settlement-types/'
    }),
    getChangeRates: builder.query<Option[], void>({
      query: () => 'change-rates/'
    }),
    getRefrigerantTypes: builder.query<Option[], void>({
      query: () => 'refrigerant-types/'
    }),
    getPackagingMaterialTypes: builder.query<Option[], void>({
      query: () => 'packaging-material-types/'
    }),
    getInputTypes: builder.query<InputTypeOption[], void>({
      query: () => "input-types/",
    }),
    getInputTypeById: builder.query<InputTypeOption, number>({
      query: (id) => `input-types/${id}/`,
      providesTags: (_res, _error, id) => [{ type: "InputTypes", id }],
    }),
    getDataSourceTypes: builder.query<Option[], number | undefined>({
      query: (moduleType) => `data-sources/${moduleType ? `?module_type=${moduleType}` : ''}`,
      transformResponse: (response: DataSourceOption[]) => response.map((opt) => ({ ...opt, name: opt.short_name }))
    }),
    getStatusTypes: builder.query<ValuedOption[], void>({
      query: () => "status-types/",
    }),
  });

  return apiSlice.injectEndpoints({ endpoints });
} 